import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Card, CardContent, ErrorList, TextInput, Typography } from '@guildeducationinc/recess';
import KeyboardArrowDownIcon from '@guildeducationinc/recess/icons/KeyboardArrowDownIcon';
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, FormControlLabel, } from '@mui/material';
import { RuleTargetFilter } from '@src/components/Admin/PolicyBuilder/RuleTargetFilter';
import { ClientPicker } from '@src/components/ClientPicker';
import { useClients } from '@src/hooks/useClients';
import { AttestationType, BenefitPeriodPopulationEntityType, EmailContentCode, WebpageContentCode, emptyContent, emptyContentItem, } from '@src/types';
import { cleanCriteria, hasIncompleteConditions, hasValidConditions, isEveryoneCriteria, } from '@src/utilities/criteriaHelper';
import _, { isEmpty } from 'lodash';
import { Confirm, ContentType, DatePicker, EnumPicker, getLanguageDisplayName, HtmlEditor, MessageType, Messages, RuleTargetType, SAVE_CONTENT, SUPPORTED_LOCALES, TrackableButton, emptyFilterGroup, useContentItems, useContents, useLocale, } from 'pe-core-frontend';
import { useEffect, useMemo, useState } from 'react';
export const CommunicationContentForm = ({ clientId, contentId, contentType, type = '', isAttestation = false, onSave: onSaveCallback, onCancel: onCancelCallback, title, contentCode, }) => {
    const { t } = useLocale();
    const { clients } = useClients({ initialPageSize: 100 });
    const { find } = useContents({
        clientId,
        id: contentId,
    });
    const savedContent = find(contentId || 0);
    // Fetch all contents to check for duplicates and find default content
    const { contents: allContents } = useContents({
        contentType,
        includeInactive: true,
    });
    const [fieldErrors, setFieldErrors] = useState({});
    // Default to Guild (clientId=1) when creating new content
    const [employerClientId, setEmployerClientId] = useState(contentId ? undefined : 1);
    const getEmptyContent = (contentType) => {
        return {
            ...emptyContent,
            contentType,
            code: contentCode || (contentType === ContentType.OTHER ? `${type}-${Date.now()}` : ''),
            criteria: undefined,
            extraData: {
                ...emptyContent.extraData,
                type,
            },
            isActive: true,
        };
    };
    const languages = useMemo(() => {
        // Webpages only support English. Attestations are translated server-side via
        // content_items (not Weglot), so admins can author them in every supported
        // language, same as emails.
        if (contentType === ContentType.WEBPAGE) {
            return ['en'];
        }
        // Support all languages defined in SUPPORTED_LOCALES
        // Extract unique language codes (e.g., 'en_US' -> 'en', 'es_MX' -> 'es')
        const allLanguages = SUPPORTED_LOCALES.map(locale => locale.locale.split('_')[0]);
        const uniqueLanguages = [...new Set(allLanguages)]; // Remove duplicates (en_US and en_UK both map to 'en')
        // Sort alphabetically by language display name
        return uniqueLanguages.sort((a, b) => getLanguageDisplayName(a).localeCompare(getLanguageDisplayName(b)));
    }, [contentType]);
    const [content, setContent] = useState(getEmptyContent(contentType));
    const [target, setTarget] = useState(RuleTargetType.ALL);
    const [messages, setMessages] = useState([]);
    const [saveContent] = useMutation(SAVE_CONTENT);
    const [contentItems, setContentItems] = useState({});
    // Snapshot of contentItems as loaded from the server, before any edits - used to
    // detect which languages the admin has actually touched this session (see
    // getLanguageSyncStatus / the language-sync save confirmation below).
    const [originalContentItems, setOriginalContentItems] = useState({});
    const [hasLoadedDefaultContent, setHasLoadedDefaultContent] = useState(false);
    const [confirmDuplicateEveryone, setConfirmDuplicateEveryone] = useState(false);
    // Set when saving an existing attestation with some but not all languages edited -
    // holds the edited/unedited language lists so the confirm dialog can name them.
    const [languageSyncConfirm, setLanguageSyncConfirm] = useState(null);
    const [ruleObj, setRuleObj] = useState({
        target: isEveryoneCriteria(content.criteria) ? RuleTargetType.ALL : RuleTargetType.FILTER,
        criteria: { ...(content.criteria || { ...emptyFilterGroup }) },
    });
    const handleRuleChange = (rule) => {
        // Clear validation errors when user starts editing
        if (fieldErrors.criteria) {
            setFieldErrors({ ...fieldErrors, criteria: '' });
        }
        setTarget(rule.target ? rule.target : target);
        if (rule.target === RuleTargetType.ALL) {
            setContent({ ...content, criteria: undefined });
            setRuleObj({ ...rule, criteria: { ...emptyFilterGroup } });
        }
        else {
            const criteria = rule.criteria && Object.keys(rule.criteria).length > 0
                ? rule.criteria
                : { ...emptyFilterGroup };
            setContent({ ...content, criteria });
            setRuleObj({ ...rule, criteria });
        }
    };
    // Get the default content if we're creating new content
    const defaultContent = useMemo(() => {
        if (!contentId && content.code) {
            return allContents.find(c => c.code === content.code && c.clientId === 1);
        }
        return null;
    }, [contentId, content.code, allContents]);
    const contentItemsData = useContentItems({
        clientId: contentId ? clientId : defaultContent ? 1 : clientId,
        parentId: contentId || defaultContent?.id,
    });
    useEffect(() => {
        if (savedContent) {
            setContent(savedContent);
            setRuleObj({
                target: isEveryoneCriteria(savedContent.criteria)
                    ? RuleTargetType.ALL
                    : RuleTargetType.FILTER,
                criteria: savedContent.criteria || { ...emptyFilterGroup },
            });
            // Set the employer dropdown to show the current employer
            // If clientId is null or 1, show Guild (which is value 1)
            setEmployerClientId(savedContent.clientId && savedContent.clientId > 1 ? savedContent.clientId : 1);
            setHasLoadedDefaultContent(true);
        }
    }, [savedContent]);
    // When code changes and we're creating new content, load default content from Guild
    useEffect(() => {
        if (!contentId && defaultContent && !hasLoadedDefaultContent) {
            // Load the default content but keep the new ID and reset clientId
            setContent({
                ...defaultContent,
                id: 0, // This is a new content
                clientId: 0, // Will be set by employerClientId
                name: defaultContent.name, // Will be updated when employer is selected
            });
            setHasLoadedDefaultContent(true);
        }
    }, [defaultContent, contentId, hasLoadedDefaultContent]);
    // Update name when employer is selected for new content
    useEffect(() => {
        if (!contentId && content.code && employerClientId && defaultContent) {
            const selectedClient = clients.find(c => c.id === employerClientId);
            const employerName = selectedClient?.name || `Client ${employerClientId}`;
            // Update the name: "{content code name} for {employer name}"
            const newName = `${defaultContent.name} for ${employerName}`;
            setContent(prev => ({ ...prev, name: newName }));
        }
    }, [employerClientId, contentId, content.code, defaultContent, clients]);
    useEffect(() => {
        const contentItems = {};
        languages.forEach(x => {
            contentItems[x] = {
                ...emptyContentItem,
                language: x,
            };
        });
        if (!contentItemsData.loading && (contentItemsData.items?.length ?? 0) > 0) {
            contentItemsData.items?.forEach(x => {
                contentItems[x.language] = x;
            });
        }
        setContentItems(contentItems);
        setOriginalContentItems(contentItems);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languages, contentItemsData.loading, employerClientId, contentId, clients]);
    const validate = () => {
        const errors = {};
        if (!content.name) {
            errors.name = t('{field} is required', { values: { field: t('Name') } });
        }
        if (!content.code) {
            errors.code = t('{field} is required', { values: { field: t('Code') } });
        }
        if (isAttestation && !content.extraData.attestationType) {
            errors.attestationType = t('{field} is required', {
                values: { field: t('Attestation Type') },
            });
        }
        if (isAttestation) {
            const emptyLanguages = languages.filter(language => !contentItems[language]?.value?.trim());
            if (emptyLanguages.length > 0) {
                errors.contentItems = `Attestation text is required for: ${emptyLanguages
                    .map(getLanguageDisplayName)
                    .join(', ')}`;
            }
        }
        if (content.startsOn && content.expiresOn && content.startsOn > content.expiresOn) {
            errors.expiresOn = t('{field1} cannot be before {field2}', {
                values: { field1: t('Expires on'), field2: t('Starts on') },
            });
        }
        // Validate filter conditions if target is FILTER
        if (target === RuleTargetType.FILTER) {
            const criteria = content.criteria || null;
            if (hasIncompleteConditions(criteria)) {
                errors.criteria = 'All filter conditions must be completed before saving';
            }
            else if (!hasValidConditions(criteria)) {
                errors.criteria =
                    'At least one complete filter condition is required when using Specific Conditions';
            }
        }
        // Check for duplicate code with same employer (only for email content types)
        const targetClientId = employerClientId || clientId;
        if (content.contentType === ContentType.EMAIL && content.code && targetClientId) {
            const existingContent = allContents.find(c => c.code === content.code && c.clientId === targetClientId && c.id !== content.id);
            if (existingContent) {
                errors.code = t('A content with this code and employer already exists');
            }
        }
        setFieldErrors(errors);
        return isEmpty(errors);
    };
    // Which languages differ from what was loaded from the server - used to warn admins
    // when they're about to save an attestation update that only touched some languages,
    // since attestation translations are legal language that should stay in sync.
    const getLanguageSyncStatus = () => {
        const isLanguageEdited = (language) => contentItems[language]?.value !== originalContentItems[language]?.value ||
            contentItems[language]?.title !== originalContentItems[language]?.title;
        return {
            editedLanguages: languages.filter(isLanguageEdited),
            uneditedLanguages: languages.filter(language => !isLanguageEdited(language)),
        };
    };
    const handleSave = async (skipDuplicateCheck = false, skipLanguageSyncConfirm = false) => {
        if (!validate()) {
            return;
        }
        // Warn (but don't block) when editing an existing attestation and only some
        // languages were touched - give the admin a chance to go update the rest, or
        // save anyway if the change genuinely doesn't need translating (e.g. a typo fix).
        if (isAttestation && content.id > 0 && !skipLanguageSyncConfirm) {
            const { editedLanguages, uneditedLanguages } = getLanguageSyncStatus();
            if (editedLanguages.length > 0 && uneditedLanguages.length > 0) {
                setLanguageSyncConfirm({ editedLanguages, uneditedLanguages });
                return;
            }
        }
        const targetClientId = employerClientId || clientId;
        // Check for duplicate "everyone" rules for the same code and employer, and confirm before proceeding
        if (!skipDuplicateCheck && target === RuleTargetType.ALL) {
            const existingEveryoneContent = allContents.find(c => (c.criteria === undefined || c.criteria === null) &&
                c.code === content.code &&
                c.clientId === targetClientId &&
                c.id !== content.id);
            if (existingEveryoneContent) {
                setConfirmDuplicateEveryone(true);
                return;
            }
        }
        // Clean criteria before saving - remove incomplete conditions
        // When target is ALL (everyone), send undefined criteria
        let criteria = undefined;
        if (target === RuleTargetType.FILTER && content.criteria) {
            criteria = cleanCriteria(content.criteria);
        }
        await saveContent({
            variables: {
                input: {
                    ..._.omit(content, [
                        'id',
                        'createdAt',
                        'updatedAt',
                        '__typename',
                        'contentItems',
                        'criteria',
                    ]),
                    criteria,
                    id: content.clientId === targetClientId ? content.id : null,
                    clientId: targetClientId,
                    items: Object.values(contentItems).map(x => {
                        return {
                            ..._.omit(x, ['id', 'createdAt', 'updatedAt', '__typename']),
                            id: x.clientId === targetClientId ? x.id : null,
                            clientId: targetClientId,
                            code: content.code,
                        };
                    }),
                },
            },
        });
        setMessages([{ type: MessageType.SUCCESS, value: 'Content saved' }]);
        if (onSaveCallback) {
            onSaveCallback();
        }
    };
    const handleCancel = () => {
        if (onCancelCallback) {
            onCancelCallback();
        }
    };
    const availablePlaceholders = useMemo(() => {
        let placeholders = ['{client_code}', '{first_name}', '{last_name}'];
        if ([
            EmailContentCode.PAYMENT_APPROVED,
            EmailContentCode.PAYMENT_REJECTED,
            EmailContentCode.PAYMENT_INFO_NEEDED,
            EmailContentCode.PAYMENT_REQUEST_APPROVED,
            EmailContentCode.PAYMENT_REQUEST_REJECTED,
            EmailContentCode.PAYMENT_REQUEST_INFO_NEEDED,
            EmailContentCode.PAYMENT_REQUEST_APPROVED_PREIMBURSEMENT,
            EmailContentCode.PAYMENT_REQUEST_REJECTED_PREIMBURSEMENT,
            EmailContentCode.PAYMENT_REQUEST_INFO_NEEDED_PREIMBURSEMENT,
            EmailContentCode.PAYMENT_REQUEST_APPROVAL_TO_APPROVER,
        ].includes(content.code)) {
            placeholders.push('{employer_name}', '{program_name}', '{expense_type}', '{payment_request_url}', '{school_name}', '{request_num}', '{amount_requested}', '{amount_approved}', '{amount_to_pay}', '{created_at}', '{comment}', '{term_start_date}', '{term_end_date}', '{document_deadline}', '{admin_url}');
        }
        if ([
            EmailContentCode.PROGRAM_APPROVED,
            EmailContentCode.PROGRAM_REJECTED,
            EmailContentCode.PROGRAM_INFO_NEEDED,
            EmailContentCode.PROGRAM_EXPIRED,
            EmailContentCode.PROGRAM_APPROVAL_TO_APPROVER,
            EmailContentCode.PROGRAM_REPLIED_INFO_NEEDED_TO_APPROVER,
            EmailContentCode.OTHER_SCHOOL_APPROVAL_TO_APPROVER,
        ].includes(content.code)) {
            placeholders.push('{program_name}', '{program_url}', '{expense_type}', '{admin_url}');
        }
        if ([
            EmailContentCode.PROGRAM_APPROVAL_TO_APPROVER,
            EmailContentCode.PROGRAM_REPLIED_INFO_NEEDED_TO_APPROVER,
            EmailContentCode.OTHER_SCHOOL_APPROVAL_TO_APPROVER,
            EmailContentCode.PAYMENT_REQUEST_APPROVAL_TO_APPROVER,
            EmailContentCode.PAYMENT_REQUEST_REPLIED_INFO_NEEDED_TO_APPROVER,
            EmailContentCode.PAYMENT_REQUEST_APPROVAL_TO_APPROVER_PREIMBURSEMENT,
            EmailContentCode.PAYMENT_REQUEST_REPLIED_INFO_NEEDED_TO_APPROVER_PREIMBURSEMENT,
        ].includes(content.code)) {
            placeholders.push('{employee_first_name}', '{employee_last_name}', '{school_name}', '{employee_full_name}');
        }
        if ([EmailContentCode.EMPLOYEE_TERMINATION].includes(content.code)) {
            placeholders.push('{{disable_at}}', '{{process_pending}}');
        }
        if ([EmailContentCode.MFA_CODE].includes(content.code)) {
            placeholders.push('{mfa_code}');
        }
        if ([EmailContentCode.SIGN_UP, EmailContentCode.RESET_PASSWORD].includes(content.code)) {
            placeholders.push('{sign_up_url}', '{confirmation_code}');
        }
        if ([EmailContentCode.EXPORT_COMPLETED, EmailContentCode.EXPORT_NO_FILE_SENT].includes(content.code)) {
            placeholders = [
                '{client_name}',
                '{client_code}',
                '{filename}',
                '{date}',
                '{items_count}',
                '{total_amount}',
            ];
        }
        return placeholders;
    }, [content.code]);
    const displayTitle = title || (content.id > 0 ? t('Edit Content') : t('Add Content'));
    return (_jsx(Card, { children: _jsxs(CardContent, { children: [_jsx("h4", { className: 'text-3xl pb-3', children: displayTitle }), _jsxs("div", { children: [_jsx(Messages, { messages: messages, onClose: () => setMessages([]) }), _jsxs("div", { className: 'flex flex-col mb-5 gap-3', children: [content.contentType === ContentType.EMAIL && (_jsx(EnumPicker, { label: t('Code'), labelPrefix: 'content.email', enumType: EmailContentCode, value: content.code, onChange: (value) => {
                                        setContent({ ...content, code: value });
                                        setMessages([]);
                                    }, disabled: content.id > 0 || !!contentCode, errors: fieldErrors.code })), content.contentType === ContentType.WEBPAGE && (_jsx(EnumPicker, { label: t('Code'), labelPrefix: 'content.webpage', enumType: WebpageContentCode, value: content.code, onChange: (value) => {
                                        setContent({ ...content, code: value });
                                        setMessages([]);
                                    }, disabled: content.id > 0 || !!contentCode, errors: fieldErrors.code })), isAttestation && (_jsx(EnumPicker, { label: t('Attestation Type'), labelPrefix: 'content.attestationType', enumType: AttestationType, value: content?.extraData?.attestationType || '', onChange: (value) => {
                                        setContent({
                                            ...content,
                                            extraData: { ...content.extraData, attestationType: value },
                                        });
                                        setMessages([]);
                                    }, disabled: content.id > 0, errors: fieldErrors.attestationType })), _jsx(ClientPicker, { clientId: employerClientId, onChange: clientId => {
                                        setEmployerClientId(clientId);
                                        setFieldErrors({});
                                    }, disabled: !!contentId }), _jsx(TextInput, { label: t('Name'), value: String(content.name), onChange: e => {
                                        setContent({ ...content, name: e.target.value });
                                        setMessages([]);
                                    }, errors: fieldErrors.name }), content.contentType !== ContentType.EMAIL && (_jsxs(_Fragment, { children: [_jsx(FormControlLabel, { label: t('Active?'), control: _jsx(Checkbox, { checked: content.isActive, onChange: (_, isActive) => {
                                                    setContent(prev => ({ ...prev, isActive }));
                                                    setMessages([]);
                                                } }) }), _jsx(RuleTargetFilter, { rule: ruleObj, type: 'content', onChange: rule => {
                                                handleRuleChange(rule);
                                                setMessages([]);
                                            }, clientId: clientId, targetPopulationEntityEnumType: BenefitPeriodPopulationEntityType, defaultTargetPopulationEntityType: BenefitPeriodPopulationEntityType.EMPLOYEE }), fieldErrors.criteria && (_jsx("div", { className: 'text-red-600 mt-2 p-2 bg-red-50 border border-red-200 rounded', children: fieldErrors.criteria })), _jsx(DatePicker, { label: t('Starts on'), value: content.startsOn || '', onChange: startsOn => {
                                                setContent(prev => ({ ...prev, startsOn }));
                                                setMessages([]);
                                            }, errors: fieldErrors.startsOn }), _jsx(DatePicker, { label: t('Expires on'), value: content.expiresOn || '', onChange: expiresOn => {
                                                setContent(prev => ({ ...prev, expiresOn }));
                                                setMessages([]);
                                            }, errors: fieldErrors.expiresOn })] })), content.contentType === ContentType.EMAIL && (_jsx(Typography, { variant: 'label2', children: t('Available placeholders: {placeholders}', {
                                        values: { placeholders: availablePlaceholders.join(', ') },
                                    }) })), languages.map(language => (_jsx("div", { children: contentItems[language] && (_jsxs(Accordion, { children: [_jsx(AccordionSummary, { expandIcon: _jsx(KeyboardArrowDownIcon, { size: 24 }), children: _jsx("h4", { className: 'text-xl font-medium', children: getLanguageDisplayName(language) }) }), _jsx(AccordionDetails, { children: _jsxs("div", { className: 'flex flex-col gap-4', children: [_jsx(TextInput, { label: t(content.contentType === ContentType.EMAIL ? 'Email Subject' : 'Title'), value: contentItems[language].title || '', onChange: e => {
                                                                setContentItems({
                                                                    ...contentItems,
                                                                    [contentItems[language].language]: {
                                                                        ...contentItems[language],
                                                                        title: e.target.value,
                                                                    },
                                                                });
                                                                setMessages([]);
                                                            } }), _jsx("div", { children: _jsx(HtmlEditor, { label: t(content.contentType === ContentType.EMAIL ? 'Email Body' : 'Content'), value: contentItems[language].value, onChange: x => {
                                                                    setContentItems({
                                                                        ...contentItems,
                                                                        [contentItems[language].language]: {
                                                                            ...contentItems[language],
                                                                            value: x,
                                                                        },
                                                                    });
                                                                    setMessages([]);
                                                                } }) })] }) })] })) }, language))), fieldErrors.contentItems && _jsx(ErrorList, { show: true, errors: fieldErrors.contentItems })] })] }), _jsxs("div", { className: 'flex flexJustify--space_between', children: [_jsx(TrackableButton, { btnType: 'mui', enText: 'Cancel', tag: 'content_form', onClick: handleCancel, variant: 'outlined', children: t('Cancel') }), _jsx(TrackableButton, { btnType: 'mui', enText: 'Save', tag: 'content_form', variant: 'contained', onClick: () => handleSave(), children: t('Save') })] }), confirmDuplicateEveryone && (_jsx(Confirm, { message: `A Content everyone rule already exists for the content code '${content.code}' and this employer. Are you sure you want to create another?`, onConfirm: () => {
                        setConfirmDuplicateEveryone(false);
                        // The language-sync confirm (if applicable) already ran earlier in this
                        // same save attempt - skip it here too, or it'd pop up again right after
                        // this confirm resolves.
                        handleSave(true, true);
                    }, onCancel: () => setConfirmDuplicateEveryone(false) })), languageSyncConfirm && (_jsx(Confirm, { title: 'Are you sure you want to save?', message: `Only the ${languageSyncConfirm.editedLanguages
                        .map(getLanguageDisplayName)
                        .join(', ')} copy was edited. Should the other languages (${languageSyncConfirm.uneditedLanguages
                        .map(getLanguageDisplayName)
                        .join(', ')}) be updated as well?`, okText: 'Save Anyway', cancelText: 'Cancel', onConfirm: () => {
                        setLanguageSyncConfirm(null);
                        handleSave(false, true);
                    }, onCancel: () => setLanguageSyncConfirm(null) }))] }) }));
};
